/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import * as React from "react";

import { cn } from "@/lib/utils";

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("rounded-xl border bg-card text-card-foreground shadow", className)}
      {...props}
    />
  )
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  )
);
CardHeader.displayName = "CardHeader";

export type CardTitleAs = "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

/**
 * Defaults to `h2` so card titles have semantic heading markup and a valid page heading sequence.
 * Use `as="div"` only when a title is decorative and should not appear in the heading outline.
 *
 * `ref` narrows with `as`: e.g. `as="h2"` + `useRef<HTMLHeadingElement>(null)` is type-safe.
 */
export type CardTitleProps<T extends CardTitleAs = "h2"> = Omit<
  React.ComponentPropsWithoutRef<T>,
  "as"
> & {
  as?: T;
};

/** Union of all supported `CardTitle` prop shapes (for `React.ComponentProps<typeof CardTitle>` etc.). */
export type CardTitlePropsUnion = {
  [K in CardTitleAs]: CardTitleProps<K>;
}[CardTitleAs];

function CardTitleImpl(
  props: CardTitlePropsUnion,
  ref: React.Ref<HTMLDivElement | HTMLHeadingElement>
): React.ReactElement | null {
  const { as: Tag = "h2", className, ...rest } = props;
  const Comp = Tag as React.ElementType;
  return (
    <Comp
      ref={ref as React.Ref<never>}
      className={cn("font-semibold leading-none tracking-tight", className)}
      {...rest}
    />
  );
}

const CardTitle = React.forwardRef<HTMLDivElement | HTMLHeadingElement, CardTitlePropsUnion>(
  CardTitleImpl
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  )
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  )
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
